import { Alert, alpha, Box, CircularProgress, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { subscriptionQueryKeys } from '../../../../../hooks/subscription/query-keys';
import { useSubscription } from '../../../../../hooks/subscription/use-subscription';
import {
  GoodsReceivingService,
  type IGoodsReceivingService,
} from '../../../../../services/goods-receiving/goods-receiving.service';
import { colors } from '../../../../../theme/ai-theme';
import type { AddItemFormData } from '../../../hooks/use-add-item-form';
import { useExtractedData } from '../../extracted-data-context';
import { PhotoUpload, type PhotoUploadHandle } from '../../photo-upload/photo-upload';
import { classifyUploadFailure, getHttpStatusFromError } from '../../upload-failure-message';
import { UploadOptionCard } from '../upload-option-card';

interface PhotoStepProps {
  isEditMode?: boolean;
}

export function PhotoStep({ isEditMode = false }: PhotoStepProps) {
  const { t } = useTranslation('goodsReceiving');
  const queryClient = useQueryClient();
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AddItemFormData>();
  const labelPhotos = watch('labelPhotos');
  const error = errors.labelPhotos?.message as string | undefined;

  const { setExtractedData } = useExtractedData();
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionMessage, setExtractionMessage] = useState<{
    severity: 'info' | 'warning';
    text: string;
  } | null>(null);
  const [lastExtractionSucceeded, setLastExtractionSucceeded] = useState(false);

  const { aiExtractionLimit, currentWeekUsage } = useSubscription();
  const remainingExtractions = Math.max(0, aiExtractionLimit - currentWeekUsage);
  const aiQuotaExhausted = remainingExtractions === 0;

  const goodsReceivingService = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const photoUploadRef = useRef<PhotoUploadHandle>(null);

  useEffect(() => {
    if (!labelPhotos || labelPhotos.length === 0) {
      setExtractedData(null);
      setExtractionMessage(null);
      setLastExtractionSucceeded(false);
    }
  }, [labelPhotos, setExtractedData]);

  const handlePhotoChange = (photos: typeof labelPhotos) => {
    setValue('labelPhotos', photos, { shouldValidate: true });
    setExtractionMessage(null);
  };

  const triggerExtraction = (photo: File) => {
    setIsExtracting(true);
    setExtractionMessage(null);
    setLastExtractionSucceeded(false);
    goodsReceivingService
      .extractItemData(photo)
      .then((data: Awaited<ReturnType<typeof goodsReceivingService.extractItemData>>) => {
        if (data) {
          setExtractedData({
            productName: data.productName,
            quantity: data.quantity,
            unit: data.unit,
            batchNumber: data.batchNumber,
            expiryDate: data.expiryDate,
            allergens: data.allergens ?? null,
          });
          setLastExtractionSucceeded(true);
          return;
        }
        setExtractedData({
          productName: null,
          quantity: null,
          unit: null,
          batchNumber: null,
          expiryDate: null,
          allergens: null,
        });
        setExtractionMessage({ severity: 'info', text: t('extractionPartialInfo') });
      })
      .catch((err: unknown) => {
        const kind = classifyUploadFailure(getHttpStatusFromError(err));
        if (kind === 'rate-limit') {
          setExtractionMessage({ severity: 'warning', text: t('photoExtractionRateLimit') });
        } else if (kind === 'network') {
          setExtractionMessage({ severity: 'warning', text: t('photoExtractionNetworkError') });
        } else {
          Sentry.captureException(err, {
            tags: { extraction_step: 'product-photo' },
          });
          setExtractionMessage({ severity: 'info', text: t('extractionTechnicalInfo') });
        }
      })
      .finally(() => {
        setIsExtracting(false);
        queryClient.setQueryData<{ subscription: { currentWeekUsage: number } }>(
          subscriptionQueryKeys.full(),
          (old) => {
            if (!old) {
              return old;
            }
            return {
              ...old,
              subscription: {
                ...old.subscription,
                currentWeekUsage: old.subscription.currentWeekUsage + 1,
              },
            };
          },
        );
      });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {isExtracting && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            border: 1,
            borderColor: alpha(colors.primary.main, 0.3),
            bgcolor: alpha(colors.primary.main, 0.04),
            borderRadius: 2,
            px: 2,
            py: 1.5,
          }}
        >
          <CircularProgress size={18} sx={{ color: colors.primary.main }} />
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('photoExtracting')}
          </Typography>
        </Box>
      )}
      {!isExtracting && lastExtractionSucceeded && (
        <Alert severity="info">{t('aiDisclaimerItemData')}</Alert>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <UploadOptionCard
          variant="ai"
          isEditMode={isEditMode}
          aiQuotaExhausted={aiQuotaExhausted}
          busy={isExtracting}
          onUpload={() => photoUploadRef.current?.openAiFileDialog()}
        />
        <UploadOptionCard
          variant="plain"
          isEditMode={isEditMode}
          busy={isExtracting}
          onUpload={() => photoUploadRef.current?.openFileDialog()}
        />
      </Box>
      {extractionMessage && (
        <Alert severity={extractionMessage.severity}>{extractionMessage.text}</Alert>
      )}
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
      <PhotoUpload
        ref={photoUploadRef}
        value={labelPhotos || []}
        onChange={handlePhotoChange}
        maxFiles={5}
        label={t('photoUploadLabel')}
        acceptPdf
        enableAiUpload
        onAiExtractRequested={triggerExtraction}
        aiButtonDisabled={aiQuotaExhausted || isExtracting}
        hideHeader
        hideHints
        hideEmptyState
      />
    </Box>
  );
}
